<template>
  <section class="upload-details">
    <header>
      <div class="flow-btn" v-if="!viewonly">
        <button @click="previousStep">{{ $t('buttons.back') }}</button>
      </div>
      <div class="title">
        <h1>{{ $t('create.reviewTitle') }}:</h1>
        <p>{{ $t('create.reviewSubtitle') }}</p>
      </div>
      <div class="flow-btn" v-if="!viewonly">
        <button :class="{'alt-btn': hasError }" @click="nextStep">{{ $t(saveLabel) }}</button>
      </div>
    </header>
    <div class="content">
      <div class="left">
        <div class="content-group platform">
          <router-link v-if="!viewonly" :to="`/media/${$route.params.media}/platform`">
            <span v-if="platform">{{ $t('buttons.edit') }}</span>
            <span v-else>{{ $t('buttons.revisit') }}</span>
          </router-link>
          <h2>{{ $t('create.reviewUploadingTo') }}:</h2>
          <p>
            <strong>{{ platform }}</strong>
          </p>
        </div>
        <div class="content-group tags" :class="{ error: !checkedTags }">
          <router-link v-if="!viewonly" :to="`/media/${$route.params.media}/hmi`">
            <span v-if="checkedTags">{{ $t('buttons.edit') }}</span>
            <span v-else>{{ $t('buttons.revisit') }}</span>
          </router-link>
          <h2>{{ $t('create.reviewUploadingTo') }}:</h2>
          <p>
            <strong v-if="checkedTags">{{ checkedTags }}</strong>
            <strong v-else>({{ $tc('create.reviewNoTags', 2) }})</strong>
          </p>
        </div>
        <div class="content-group name" :class="{ error: !name }">
          <router-link v-if="!viewonly" :to="`/media/${$route.params.media}/name`">
            <span v-if="name">{{ $t('buttons.edit') }}</span>
            <span v-else>{{ $t('buttons.revisit') }}</span>
          </router-link>
          <h2>{{ $t('media.name') }}:</h2>
          <p>
            <strong v-if="name">{{ name }}</strong>
            <strong v-else>({{ $t('create.reviewNoName') }})</strong>
          </p>
        </div>
        <div class="content-group dates" :class="{ error: !liveDate }">
          <router-link v-if="!viewonly" :to="`/media/${$route.params.media}/dates`">
            <span v-if="name">{{ $t('buttons.edit') }}</span>
            <span v-else>{{ $t('buttons.revisit') }}</span>
          </router-link>
          <h2>{{ $t('media.goLiveDate') }}:</h2>
          <p>
            <strong v-if="liveDate">{{ liveDate | date }}</strong>
            <strong v-else>({{ $t('create.reviewNoGoLive') }})</strong>
          </p>
          <h2>{{ $t('create.datesExpire') }}:</h2>
          <p>
            <strong v-if="expirationDate && expirationDate !== Infinity">{{ expirationDate | date }}</strong>
            <strong v-else>({{ $t('create.reviewNoExpire') }})</strong>
          </p>
        </div>
        <div class="content-group tags" v-if="checkedTags" :class="tagsError">
          <router-link v-if="!viewonly" :to="`/media/${$route.params.media}/details`">
            <span v-if="name">{{ $t('buttons.edit') }}</span>
            <span v-else>{{ $t('buttons.revisit') }}</span>
          </router-link>
          <template v-for="tag in tags">
            <div :key="tag.id" v-if="tag.checked" class="tag">
              <h2>{{ tag.label }}:</h2>
              <p>
                <strong
                  v-if="tag.value && (tag.value.name || tag.value.subscriptionPlanName || tag.value.length)"
                >{{ formatTags(tag.value) }}</strong>
                <strong v-else>({{ $tc('create.reviewNoTag', 1) }})</strong>
              </p>
            </div>
          </template>
        </div>
      </div>
      <div class="right">
        <div class="screens" ref="screens">
          <template v-if="screen1EnChecked || screen2EnChecked || screen3EnChecked || screen4EnChecked">
            <div class="screen-title" v-if="$store.state.media.country === 'CAN'">
              <div class="line" />
              <div class="title">Images (English)</div>
              <div class="line" />
            </div>
            <upload-screen
              v-if="screen1EnChecked"
              :value="screen1EnChecked"
              :files="screen1FilesEn"
              :list="screen1ListEn"
              :label="$t('media.screen') + ' 1 (' + $t('media.screensaver') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="en"
            />
            <upload-screen
              v-if="screen2EnChecked"
              :value="screen2EnChecked"
              :files="screen2FilesEn"
              :list="screen2ListEn"
              :label="$t('media.screen') + ' 2 (' + $t('media.presentPayment') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="en"
            />
            <upload-screen
              v-if="screen3EnChecked"
              :value="screen3EnChecked"
              :files="screen3FilesEn"
              :list="screen3ListEn"
              :label="$t('media.screen') + ' 3 (' + $t('media.payment') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="en"
            />
            <upload-screen
              v-if="screen4EnChecked"
              :value="screen4EnChecked"
              :files="screen4FilesEn"
              :list="screen4ListEn"
              :label="$t('media.screen') + ' 4 (' + $t('media.thankYou') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="en"
            />
          </template>
          <template v-if="(screen1FrChecked || screen2FrChecked || screen3FrChecked || screen4FrChecked) && $store.state.media.country === 'CAN'">
            <div class="screen-title" style="margin-top: 50px;">
              <div class="line" />
              <div class="title">Images (French)</div>
              <div class="line" />
            </div>
            <upload-screen
              v-if="screen1FrChecked"
              :value="screen1FrChecked"
              :files="screen1FilesFr"
              :list="screen1ListFr"
              :label="$t('media.screen') + ' 1 (' + $t('media.screensaver') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="fr"
            />
            <upload-screen
              v-if="screen2FrChecked"
              :value="screen2FrChecked"
              :files="screen2FilesFr"
              :list="screen2ListFr"
              :label="$t('media.screen') + ' 2 (' + $t('media.presentPayment') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="fr"
            />
            <upload-screen
              v-if="screen3FrChecked"
              :value="screen3FrChecked"
              :files="screen3FilesFr"
              :list="screen3ListFr"
              :label="$t('media.screen') + ' 3 (' + $t('media.payment') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="fr"
            />
            <upload-screen
              v-if="screen4FrChecked"
              :value="screen4FrChecked"
              :files="screen4FilesFr"
              :list="screen4ListFr"
              :label="$t('media.screen') + ' 4 (' + $t('media.thankYou') + ')'"
              :viewonly="true"
              :edit-link="!viewonly ? `/media/${$route.params.media}/details` : null"
              language="fr"
            />
          </template>
          <div class="content-group error" v-if="!(screen1EnChecked || screen2EnChecked || screen3EnChecked || screen4EnChecked)">
            <router-link v-if="!viewonly" :to="`/media/${$route.params.media}/details`">{{ $t('buttons.revisit') }}</router-link>
            <div>
              <strong>({{ $t('create.reviewNoMedia') }})</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
    <footer v-if="!viewonly">
      <button @click="previousStep">{{ $t('buttons.back') }}</button>
      <button :class="{'alt-btn': hasError }" @click="nextStep">{{ $t(saveLabel) }}</button>
    </footer>
  </section>
</template>

<script>
import UploadScreen from '@/components/UploadScreen.vue'
import { SAVE_MEDIA, GET_CRITERIA } from '@/store/actions.type'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'upload-review',
  components: {
    UploadScreen
  },
  props: {
    viewonly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      name: state => state.media.name,
      liveDate: state => state.media.liveDate,
      expirationDate: state => state.media.expirationDate,
      expirationDateSelected: state => state.media.expirationDateSelected,
      screen1ListEn: state => state.media.screen1ListEn,
      screen2ListEn: state => state.media.screen2ListEn,
      screen3ListEn: state => state.media.screen3ListEn,
      screen4ListEn: state => state.media.screen4ListEn,
      screen1ListFr: state => state.media.screen1ListFr,
      screen2ListFr: state => state.media.screen2ListFr,
      screen3ListFr: state => state.media.screen3ListFr,
      screen4ListFr: state => state.media.screen4ListFr,
      screen1EnChecked: state => state.media.screen1EnChecked,
      screen2EnChecked: state => state.media.screen2EnChecked,
      screen3EnChecked: state => state.media.screen3EnChecked,
      screen4EnChecked: state => state.media.screen4EnChecked,
      screen1FrChecked: state => state.media.screen1FrChecked,
      screen2FrChecked: state => state.media.screen2FrChecked,
      screen3FrChecked: state => state.media.screen3FrChecked,
      screen4FrChecked: state => state.media.screen4FrChecked,
    }),
    ...mapGetters({
      platform: 'platform',
      tags: 'tags',
      screen1FilesEn: 'screen1En',
      screen2FilesEn: 'screen2En',
      screen3FilesEn: 'screen3En',
      screen4FilesEn: 'screen4En',
      screen1FilesFr: 'screen1Fr',
      screen2FilesFr: 'screen2Fr',
      screen3FilesFr: 'screen3Fr',
      screen4FilesFr: 'screen4Fr',
    }),
    checkedTags() {
      const tempTags = []
      this.$store.getters.tags.forEach(tag => {
        if (tag.checked) tempTags.push(tag.label)
      })
      return tempTags.join(', ')
    },
    tagsError() {
      for (const tag of this.$store.getters.tags) {
        if (tag.checked && ((Array.isArray(tag.value) && !tag.value.length) || !tag.value)) return 'error'
      }
      return null
    },
    hasError() {
      return (
        !this.checkedTags ||
        !this.name ||
        !this.liveDate ||
        this.tagsError ||
        !(
          this.screen1EnChecked ||
          this.screen2EnChecked ||
          this.screen3EnChecked ||
          this.screen4EnChecked
        )
      )
    },
    saveLabel() {
      return this.hasError ? 'buttons.skipSaveDraft' : 'buttons.savePublish'
    }
  },
  methods: {
    previousStep() {
      this.$emit('back')
    },
    nextStep() {
      const loader = this.$loading.show()
      this.$store
        .dispatch(SAVE_MEDIA, !this.hasError)
        .then(() => {
          loader.hide()
          this.$emit('next')
        })
        .catch(err => {
          loader.hide()
          window.alert(err)
        })
    },
    formatTags(tags) {
      if (Array.isArray(tags)) return tags.map(tag => tag.name).join(', ')
      return tags.name || tags.subscriptionPlanName
    }
  },
  created() {
    this.$store.dispatch(GET_CRITERIA)
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.content {
  display: flex;

  & > .left {
    flex: 1 0 auto;
  }
  & > .right {
    flex: 1 0 875px;
    margin-left: 25px;
    // max-width: 875px;
  }
}
.content-group {
  text-align: left;
  background: #fff;
  margin-bottom: 25px;
  padding: 50px;
  max-width: 400px;

  & > a {
    float: right;
  }

  &.error {
    color: #eb3323;
    border: 1px solid #eb3323;

    h2 {
      color: #eb3323;
    }
  }
}
.content {
  h2 {
    color: #898989;
    font-size: 1em;
    font-weight: 300;
    display: block;
  }
  p {
    margin: 1em 0 0;
  }
  p + h2,
  .tag + .tag {
    margin-top: 40px;
  }
}
.screens {
  min-height: 150px;
  position: relative;

  .screen-title {
    display: flex;
    align-items: center;
    max-width: 975px;
    margin: 0 auto 40px auto;

    .title {
      font: 600 16px/21px Omnes;
      letter-spacing: 0.4px;
      color: #0B0F4D;
      opacity: 1;
      margin: 0 25px;
    }

    .line {
      flex-grow: 1;
      border: 1px solid #ADADAD;
    }
  }

  .content-group {
    max-width: none;
  }
}
.screen {
  margin: 0 auto 25px;
  padding: 50px;
  max-width: 875px;
}
footer {
  display: flex;
  justify-content: space-between;
}
</style>
